<template>
    <service-card
        ref="serviceCard"
        :service-tips="serviceTips"
        :service="formattedService"
        icon="mdi-lightning-bolt-outline"
        :is-first="isFirst"
        :extra-service-name="info.serviceName"
        :order="order"
        :variants-available="variantsAvailable"
        @modify-service="$emit('modify-service', $event)"
        @show-info="showInfo"
        @minimized-change="minimizedChange"
    >
        <template v-slot:checkbox>
            <slot name="checkbox" />
        </template>
        <template v-slot:icons>
            <slot name="icons" />
        </template>
        <template v-slot:orderInfo>
            <order-info-base
                :service-check-in="serviceCheckIn"
                :service-check-out="serviceCheckOut"
                :service="formattedService"
                :service-image="mainImage"
                :order-end-date="orderEndDate"
                :tariff-name="service.serviceDetails[0].tariffName"
                :is-package="isPackage"
                :service-label="''"
                :order="order"
                :info="info"
                @cancel-service="$emit('cancel-service', $event)"
                @modify-service="$emit('modify-service', $event)"
                @update-linked-hotel="updateProductInfo($event)"
            >
                <template v-if="offlineServiceType === 'ACCOMMODATION' && info && info.address" v-slot:additionalInfo>
                    <hotel-address-label
                        :is-show-label="true"
                        :address="hotelAddress"
                        :hide-show-on-map-button="!info.latitude && !info.longitude"
                        @click="mapModal = true"
                    />
                </template>
                <template v-slot:subtitle>
                    <div v-if="roomType" class="d-flex align-top font-weight-bold">
                        <v-icon size="small" start>mdi-bed-empty</v-icon>
                        <span v-if="service.serviceDetails[0]?.quantityOfRooms > 1">
                            {{ `${roomType} x ${service.serviceDetails[0].quantityOfRooms}` }}
                        </span>
                        <span v-else>{{ roomType }}</span>
                    </div>
                </template>
                <template v-slot:extraTime>
                    <activity-duration
                        v-if="durationDays || durationTime"
                        :is-show-label="true"
                        :time="durationTime"
                        :days="durationDays"
                    />
                </template>
                <div v-if="offlineServiceType === 'ACCOMMODATION'" class="order-info-base-info">
                    <span class="order-info-base-info__icon">
                        <v-icon start size="x-small">mdi-weather-night</v-icon>
                        <span class="text-body-2 mr-2 text-capitalize">{{ $t('night', nights) }}</span>
                    </span>
                    <span class="order-info-base-info__text">
                        {{ `${nights} ${$t('night', nights)}` }}
                    </span>
                </div>
                <div v-if="mealType && offlineServiceType !== 'ACCOMMODATION'" class="order-info-base-info">
                    <span class="order-info-base-info__icon">
                        <v-icon size="x-small" start>mdi-silverware-fork-knife</v-icon>
                        <span class="text-body-2">{{ $t('product.meal_type') }}</span>
                    </span>
                    <span class="order-info-base-info__text">
                        {{ mealType }}
                    </span>
                </div>
                <div v-if="departureAirportCode" class="order-info-base-info">
                    <span class="order-info-base-info__icon">
                        <v-icon size="x-small" start>mdi-airplane-takeoff</v-icon>
                        <span class="text-body-2">{{ $t('flights.departure_airport') }}</span>
                    </span>
                    <span class="order-info-base-info__text">
                        {{ departureAirportCode }}
                    </span>
                </div>
                <div v-if="departureTerminal" class="order-info-base-info">
                    <span class="order-info-base-info__icon">
                        <v-icon size="x-small" start>mdi-airport</v-icon>
                        <span class="text-body-2">{{ $t('flights.departure_terminal') }}</span>
                    </span>
                    <span class="order-info-base-info__text">
                        {{ departureTerminal }}
                    </span>
                </div>
                <div v-if="arrivalAirportCode" class="order-info-base-info">
                    <span class="order-info-base-info__icon">
                        <v-icon size="x-small" start>mdi-airplane-landing</v-icon>
                        <span class="text-body-2">{{ $t('flights.arrival_airport') }}</span>
                    </span>
                    <span class="order-info-base-info__text">
                        {{ arrivalAirportCode }}
                    </span>
                </div>
                <div v-if="arrivalTerminal" class="order-info-base-info">
                    <span class="order-info-base-info__icon">
                        <v-icon size="x-small" start>mdi-airport</v-icon>
                        <span class="text-body-2">{{ $t('flights.arrival_terminal') }}</span>
                    </span>
                    <span class="order-info-base-info__text">
                        {{ arrivalTerminal }}
                    </span>
                </div>
                <div v-if="flightNumber" class="order-info-base-info">
                    <span class="order-info-base-info__icon">
                        <v-icon size="x-small" start> mdi-airplane</v-icon>
                        <span class="text-body-2">{{ $t('flights.flight_number') }}</span>
                    </span>
                    <span class="order-info-base-info__text">
                        {{ flightNumber }}
                    </span>
                </div>
                <div v-if="flightClass" class="order-info-base-info">
                    <span class="order-info-base-info__icon">
                        <v-icon start class="date-icon" size="x-small">mdi-seat-recline-extra</v-icon>
                        <span class="text-body-2">{{ $t('flights.flight_class') }}</span>
                    </span>
                    <span class="order-info-base-info__text">
                        {{ flightClass }}
                    </span>
                </div>
                <div v-if="bookingClass" class="order-info-base-info">
                    <span class="order-info-base-info__icon">
                        <v-icon start class="date-icon" size="x-small">mdi-seat-recline-extra</v-icon>
                        <span class="text-body-2">{{ $t('flights.booking_class') }}</span>
                    </span>
                    <span class="order-info-base-info__text"> {{ bookingClass }}</span>
                </div>
                <div v-if="seatsPrebooked" class="order-info-base-info">
                    <span class="order-info-base-info__icon">
                        <v-icon size="x-small" start>mdi-seat-passenger</v-icon>
                        <span class="text-body-2">{{ $t('flights.seats_prebooked') }}</span>
                    </span>
                    <span class="order-info-base-info__text">
                        <v-icon size="small" start>mdi-check</v-icon>
                    </span>
                </div>
                <div v-if="mealIncluded" class="order-info-base-info">
                    <span class="order-info-base-info__icon">
                        <v-icon size="x-small" start>mdi-food-outline</v-icon>
                        <span class="text-body-2">{{ $t('meal_included') }}</span>
                    </span>
                    <span class="order-info-base-info__text">
                        <v-icon size="small" start>mdi-check</v-icon>
                    </span>
                </div>
                <div v-if="pickUpPoint" class="order-info-base-info">
                    <span class="order-info-base-info__icon">
                        <v-icon size="x-small" start>mdi-map-marker-outline</v-icon>
                        <span class="text-body-2">
                            {{
                                offlineServiceType === 'CAR_RENT' || offlineServiceType === 'TRANSFER'
                                    ? $t('transfers.from')
                                    : offlineServiceType === 'CRUISE'
                                      ? $t('cruises.departure_port')
                                      : $t('pick_up_point')
                            }}
                        </span>
                    </span>
                    <span class="order-info-base-info__text">
                        {{ pickUpPoint }}
                    </span>
                </div>
                <div
                    v-if="pickUpTime && offlineServiceType !== 'CAR_RENT' && offlineServiceType !== 'TRANSFER'"
                    class="order-info-base-info"
                >
                    <span class="order-info-base-info__icon">
                        <v-icon start class="date-icon" size="x-small">mdi-clock-in</v-icon>
                        <span class="text-body-2">{{ $t('pick_up_time') }}</span>
                    </span>
                    <span class="order-info-base-info__text">
                        {{ pickUpTime }}
                    </span>
                </div>
                <div v-if="dropOffPoint" class="order-info-base-info">
                    <span class="order-info-base-info__icon">
                        <v-icon size="x-small" start>mdi-map-marker-outline</v-icon>
                        <span class="text-body-2">
                            {{
                                offlineServiceType === 'CAR_RENT' || offlineServiceType === 'TRANSFER'
                                    ? $t('transfers.to')
                                    : offlineServiceType === 'CRUISE'
                                      ? $t('cruises.arrival_port')
                                      : $t('drop_off_point')
                            }}
                        </span>
                    </span>
                    <span class="order-info-base-info__text">
                        {{ dropOffPoint }}
                    </span>
                </div>
                <div
                    v-if="dropOffTime && offlineServiceType !== 'CAR_RENT' && offlineServiceType !== 'TRANSFER'"
                    class="order-info-base-info"
                >
                    <span class="order-info-base-info__icon">
                        <v-icon start class="date-icon" size="x-small">mdi-clock-out</v-icon>
                        <span class="text-body-2">{{ $t('drop_off_time') }}</span>
                    </span>
                    <span class="order-info-base-info__text">
                        {{ dropOffTime }}
                    </span>
                </div>
                <div v-if="vehicleCategory" class="order-info-base-info">
                    <span class="order-info-base-info__icon">
                        <v-icon start class="date-icon" size="x-small">mdi-car</v-icon>
                        <span class="text-body-2">{{ $t('filterTitles.vehicle_type') }}</span>
                    </span>
                    <span class="order-info-base-info__text">
                        {{ vehicleCategory }}
                    </span>
                </div>
                <div v-if="shipName" class="order-info-base-info">
                    <span class="order-info-base-info__icon">
                        <v-icon start class="date-icon" size="x-small">mdi-ship-wheel</v-icon>
                        <span class="text-body-2">{{ $t('cruises.ship_name') }}</span>
                    </span>
                    <span class="order-info-base-info__text">
                        {{ `${shipName}` }}
                    </span>
                </div>
                <div v-if="cruiseLine" class="order-info-base-info">
                    <span class="order-info-base-info__icon">
                        <v-icon start class="date-icon" size="x-small">mdi-ferry</v-icon>
                        <span class="text-body-2">{{ $t('cruises.cruise_line') }}</span>
                    </span>
                    <span class="order-info-base-info__text">
                        {{ `${cruiseLine}` }}
                    </span>
                </div>
                <div v-if="cabinNumber" class="order-info-base-info">
                    <span class="order-info-base-info__icon">
                        <v-icon start class="date-icon" size="x-small">mdi-door</v-icon>
                        <span class="text-body-2">{{ $t('cruises.cabin_number') }}</span>
                    </span>
                    <span class="order-info-base-info__text">
                        {{ `${cabinNumber}` }}
                    </span>
                </div>
                <div v-if="deckNumber" class="order-info-base-info">
                    <span class="order-info-base-info__icon">
                        <v-icon start class="date-icon" size="x-small">mdi-stairs</v-icon>
                        <span class="text-body-2">{{ $t('cruises.deck_number') }}</span>
                    </span>
                    <span class="order-info-base-info__text">
                        {{ `${deckNumber}` }}
                    </span>
                </div>
                <div v-if="deckName" class="order-info-base-info">
                    <span class="order-info-base-info__icon">
                        <v-icon start class="date-icon" size="x-small">mdi-stairs</v-icon>
                        <span class="text-body-2">{{ $t('cruises.deck_name') }}</span>
                    </span>
                    <span class="order-info-base-info__text">
                        {{ `${deckName}` }}
                    </span>
                </div>
                <div v-if="cabinCategory" class="order-info-base-info">
                    <span class="order-info-base-info__icon">
                        <v-icon start class="date-icon" size="x-small">mdi-door</v-icon>
                        <span class="text-body-2">{{ $t('cruises.cabin_category') }}</span>
                    </span>
                    <span class="order-info-base-info__text">
                        {{ `${cabinCategory}` }}
                    </span>
                </div>
                <div v-if="cabinTypeName" class="order-info-base-info">
                    <span class="order-info-base-info__icon">
                        <v-icon start class="date-icon" size="x-small">mdi-door</v-icon>
                        <span class="text-body-2">{{ $t('cruises.cabin_type_name') }}</span>
                    </span>
                    <span class="order-info-base-info__text">
                        {{ `${cabinTypeName}` }}
                    </span>
                </div>
                <div v-if="vehicleType" class="order-info-base-info">
                    <span class="order-info-base-info__icon">
                        <v-icon start class="date-icon" size="x-small">mdi-car</v-icon>
                        <span class="text-body-2">{{ $t('vehicle_type') }}</span>
                    </span>
                    <span class="order-info-base-info__text">
                        {{ `${vehicleType}` }}
                    </span>
                </div>
                <div v-if="oneWay !== undefined" class="order-info-base-info">
                    <span class="order-info-base-info__icon">
                        <v-icon start class="date-icon" size="x-small">
                            {{ oneWay ? 'mdi-arrow-right' : 'mdi-arrow-left' }}
                        </v-icon>
                        <span class="text-body-2">
                            {{ `${oneWay ? $t('transfers.one_way_or_return') : $t('transfers.return_transfer')}` }}
                        </span>
                    </span>
                    <span class="order-info-base-info__text">
                        <v-icon size="small" start>mdi-check</v-icon>
                    </span>
                </div>
                <div v-if="groupTransfer !== undefined" class="order-info-base-info">
                    <span class="order-info-base-info__icon">
                        <v-icon start class="date-icon" size="x-small">
                            {{ groupTransfer ? 'mdi-account-group-outline' : 'mdi-account-outline' }}
                        </v-icon>
                        <span class="text-body-2">
                            {{
                                `${groupTransfer ? $t('transfers.shared_transfer') : $t('transfers.private_transfer')}`
                            }}
                        </span>
                    </span>
                    <span class="order-info-base-info__text">
                        <v-icon size="small" start>mdi-check</v-icon>
                    </span>
                </div>
                <div v-if="contactNumber" class="order-info-base-info">
                    <span class="order-info-base-info__icon">
                        <v-icon size="x-small" start>mdi-phone</v-icon>
                        <span class="text-body-2">{{ $t('number_or_transfer_provider') }}</span>
                    </span>
                    <span class="order-info-base-info__text text-no-wrap">
                        {{ contactNumber }}
                    </span>
                </div>
                <template v-slot:additionalInfo2>
                    <flight-service-additional-info
                        v-if="offlineServiceType === 'FLIGHT' && (isMinimized === false || infoLoadingFinished)"
                        :service="formattedService"
                    />
                </template>
            </order-info-base>
        </template>
        <template v-slot:serviceInfo>
            <service-info-dialog v-model="modal" :service="formattedService" :info="info" :image="mainImage">
                <template v-if="offlineServiceType === 'ACCOMMODATION' && info" v-slot:serviceInfo>
                    <div v-if="hotelAddress" class="d-flex align-center">
                        <v-icon start size="small" color="info">mdi-map-marker</v-icon>
                        <small>{{ hotelAddress }}</small>
                    </div>
                    <div v-if="info.email && showContacts" class="d-flex align-center text-caption">
                        <v-icon start size="small">mdi-at</v-icon>
                        {{ info.email }}
                    </div>
                    <div v-if="info.url" class="d-flex align-center text-caption">
                        <v-icon start size="small">mdi-web</v-icon>
                        <a :href="info.url" target="_blank" class="text-caption">
                            {{ info.url }}
                        </a>
                    </div>
                    <v-row no-gutters>
                        <v-col v-if="info.phone && showContacts" :cols="12" md="12">
                            <div class="d-flex align-center text-caption">
                                <v-icon start size="small">mdi-phone</v-icon>
                                {{ info.phone }}
                            </div>
                        </v-col>
                    </v-row>
                    <v-row no-gutters>
                        <v-col v-if="info.fax && showContacts" :cols="12" md="12">
                            <div class="d-flex align-center text-caption">
                                <v-icon start size="small">mdi-fax</v-icon>
                                {{ info.fax }}
                            </div>
                        </v-col>
                    </v-row>
                </template>
                <template v-slot:extraInfo>
                    <div
                        v-if="service.serviceDetails[0].description"
                        class="make-new-line"
                        :class="{'pl-5': /<ol>|<ul>/.test(service.serviceDetails[0].description)}"
                        v-html="service.serviceDetails[0].description"
                    />
                    <div
                        v-if="info.productDescription"
                        :class="{'pl-5': /<ol>|<ul>/.test(info.productDescription)}"
                        v-html="info.productDescription"
                    />
                </template>
            </service-info-dialog>
            <client-only>
                <ModalWrapper v-if="info && info.latitude && info.longitude" v-model="mapModal" :max-width="500">
                    <map-location
                        v-if="mapModal"
                        :latitude="info.latitude"
                        :longitude="info.longitude"
                        :height="`500px`"
                    />
                </ModalWrapper>
            </client-only>
        </template>
    </service-card>
</template>

<script lang="ts">
    import {Component, Emit, Prop} from 'vue-facing-decorator'
    import ServiceCard from '@/components/account/orderServices/ServiceCard.vue'
    import ServiceInfoDialog from '~/components/account/orderServices/ServiceInfoDialog.vue'
    import {ServiceBase} from '@/components/account/orderServices/ServiceBase.vue'
    import FlightServiceAdditionalInfo from '~src/components/account/snippets/FlightServiceAdditionalInfo.vue'
    import ActivityDuration from '@/components/activities/search/offers/ActivityDuration.vue'
    import {fullUrl} from '~src/utils/host-adapter'
    import OrderInfoBase from '~src/components/account/orderServices/orderInfoBase.src'
    import MapLocation from '~src/components/info/mapLocation.src'
    import HotelAddressLabel from '~src/components/hotels/snippets/hotelAddressLabel.src'
    import {hotelAddressToString} from '~/utils/helpers'

    @Component({
        components: {
            ActivityDuration,
            FlightServiceAdditionalInfo,
            ServiceInfoDialog,
            OrderInfoBase,
            ServiceCard,
            MapLocation,
            HotelAddressLabel,
        },
    })
    export class OfflineService extends ServiceBase {
        @Prop({required: true}) service
        @Prop({default: false}) isPackage
        @Prop() isFirst

        info = {}
        modal = false
        mapModal = false

        @Emit()
        infoLoaded(info) {
            return info
        }

        @Emit()
        addMarker(latitude, longitude) {
            return {lat: latitude, lng: longitude, type: 'hotel'}
        }

        showInfo() {
            if (!this.infoLoadingFinished) {
                this.loadProductInfo()
            }

            this.modal = true
        }

        async loadProductInfo() {
            this.isInfoLoading = true

            if (this.service.serviceDetails[0]?.hotelId) {
                const rq = {hotelId: this.service.serviceDetails[0].hotelId}

                try {
                    const response = await this.$api.hotelInfo.get(rq)
                    this.info = response.hotelDescriptionInfo
                    if (this.info.latitude && this.info.longitude) {
                        this.addMarker(this.info.latitude, this.info.longitude)
                    }
                    this.infoLoaded(this.info)

                    //eslint-disable-next-line no-empty
                } catch (e) {}
            }

            this.isInfoLoading = false
            this.infoLoadingFinished = true
        }

        updateProductInfo(info) {
            this.info = info ? info : {}
        }

        get formattedService() {
            if (this.offlineServiceType === 'ACCOMMODATION' && this.info) {
                const serviceDetails = [
                    {
                        ...this.service.serviceDetails[0],
                        ...(this.info.stdCategory && {category: hotelsStore.category(this.info.stdCategory)}),
                    },
                ]

                return {
                    ...this.service,
                    serviceDetails,
                }
            }
            return this.service
        }

        get mainImage() {
            if (this.isBankFee) {
                return fullUrl('/bank-fee.jpg')
            }
            if (this.info.images && this.info.images.length) {
                let image = this.info.images.find(el => el.mainImage)
                if (!image) {
                    image = this.info.images[0]
                }
                return image.url
            }
            if (this.offlineServiceType === 'ACCOMMODATION' && this.info.images && this.info.images.image?.length) {
                let image = this.info.images.image.find(el => el.mainImage)
                if (!image) {
                    image = this.info.images.image[0]
                }
                return image.url
            }
            if (this.offlineServiceType === 'FLIGHT' && this.service.serviceDetails[0]?.marketingAirline) {
                return fullUrl(`/stat/images/airlines/${this.service.serviceDetails[0].marketingAirline}.gif`)
            }

            return null
        }

        get mealType() {
            return this.service.serviceDetails[0]?.mealType
        }

        get roomType() {
            return this.service.serviceDetails[0]?.roomType
        }

        get arrivalAirportCode() {
            return this.service.serviceDetails[0]?.arrivalAirportCode
        }

        get arrivalTerminal() {
            return this.service.serviceDetails[0]?.arrivalTerminal
        }

        get bookingClass() {
            return this.service.serviceDetails[0]?.bookingClass
        }

        get departureAirportCode() {
            return this.service.serviceDetails[0]?.departureAirportCode
        }

        get departureTerminal() {
            return this.service.serviceDetails[0]?.departureTerminal
        }

        get flightClass() {
            return this.service.serviceDetails[0]?.flightClass
        }

        get flightNumber() {
            return this.service.serviceDetails[0]?.flightNumber
        }

        get seatsPrebooked() {
            return this.service.serviceDetails[0]?.seatsPrebooked
        }

        get mealIncluded() {
            return this.service.serviceDetails[0]?.mealIncluded
        }

        get durationTime() {
            return this.service.serviceDetails[0]?.durationTime || this.service.serviceDetails[0]?.duration
        }

        get durationDays() {
            if (this.offlineServiceType === 'CRUISE') {
                return (
                    this.$dateFns.differenceInCalendarDays(
                        new Date(this.service.endDateTime),
                        new Date(this.service.startDateTime)
                    ) + 1
                )
            }

            return this.service.serviceDetails[0]?.durationDays
        }

        get pickUpPoint() {
            return this.service.serviceDetails[0]?.pickUpPoint
        }

        get pickUpTime() {
            return this.service.serviceDetails[0]?.pickUpTime || ''
        }

        get dropOffPoint() {
            return this.service.serviceDetails[0]?.dropOffPoint
        }

        get dropOffTime() {
            return this.service.serviceDetails[0]?.dropOffTime || ''
        }

        get vehicleCategory() {
            return this.service.serviceDetails[0]?.vehicleCategory
        }

        get oneWay() {
            return this.service.serviceDetails[0]?.oneWay
        }

        get groupTransfer() {
            return this.service.serviceDetails[0]?.groupTransfer
        }

        get contactNumber() {
            return this.service.serviceDetails[0]?.contactNumber
        }

        get shipName() {
            return this.service.serviceDetails[0]?.shipName
        }

        get cruiseLine() {
            return this.service.serviceDetails[0]?.cruiseLine
        }

        get cabinNumber() {
            return this.service.serviceDetails[0]?.cabinNumber
        }

        get cabinCategory() {
            return this.service.serviceDetails[0]?.cabinCategory
        }

        get cabinTypeName() {
            return this.service.serviceDetails[0]?.cabinTypeName
        }

        get deckNumber() {
            return this.service.serviceDetails[0]?.deckNumber
        }

        get deckName() {
            return this.service.serviceDetails[0]?.deckName
        }

        get vehicleType() {
            return this.service.serviceDetails[0]?.vehicleType
        }

        get description() {
            return this.service.serviceDetails[0]?.description
        }

        get nights() {
            const startDate = new Date(this.service.startDateTime.slice(0, 10))
            const endDate = new Date(this.service.endDateTime.slice(0, 10))
            return (new Date(endDate) - new Date(startDate)) / 8.64e7
        }

        get offlineServiceType() {
            return this.service.serviceDetails[0]?.extraServiceProductType
        }

        get serviceCheckIn() {
            switch (this.offlineServiceType) {
                case 'CAR_RENT':
                case 'TRANSFER':
                    return this.carRentCheckIn
                case 'FLIGHT':
                    return this.flightCheckIn
                case 'ACCOMMODATION':
                    return this.$filters.dateFormat(this.service.startDateTime)
                case 'CRUISE':
                    return this.$filters.dateFormat(this.service.startDateTime?.split(' ')[0])
            }

            return this.$filters.dateTimeFormat(this.service.startDateTime)
        }

        get serviceCheckOut() {
            switch (this.offlineServiceType) {
                case 'CAR_RENT':
                case 'TRANSFER':
                    return this.carRentCheckOut
                case 'FLIGHT':
                    return null
                case 'ACCOMMODATION':
                    return this.$filters.dateFormat(this.service.endDateTime)
                case 'CRUISE':
                    return this.$filters.dateFormat(this.service.endDateTime?.split(' ')[0])
            }

            return this.service.startDateTime !== this.service.endDateTime
                ? this.$filters.dateTimeFormat(this.service.endDateTime)
                : null
        }

        get carRentCheckIn() {
            const pickUpTime = this.pickUpTime
            const date = this.$filters.dateFormat(this.service.startDateTime)
            return `<span> ${this.$t('transfers.pick_up')}: ${date}</span> <small>${pickUpTime}</small>`
        }

        get carRentCheckOut() {
            const dropOffTime = this.dropOffTime
            const date = this.$filters.dateFormat(this.service.endDateTime)
            return `<span> ${this.$t('transfers.drop_off')}: ${date}</span> <small>${dropOffTime}</small>`
        }

        get flightCheckIn() {
            const date = this.$filters.dateFormat(this.service.startDateTime)
            return `${this.$t('flights.departure')}: ${date}`
        }

        get flightCheckOut() {
            const date = this.$filters.dateFormat(this.service.endDateTime)
            return `${this.$t('flights.arrival')}: ${date}`
        }

        get isBankFee() {
            return this.service.serviceDetails?.[0]?.extraServiceProductType === 'BANK_FEE'
        }

        get hotelAddress() {
            return hotelAddressToString(this.info?.address)
        }

        get showContacts() {
            return this.$conf.public.showContacts
        }

        getIcon(place) {
            switch (this.getPointType(place)) {
                case 'airport':
                    return 'mdi-airport'
                case 'hotel':
                    return 'mdi-hotel'
                case 'train':
                    return 'mdi-train'
                case 'port':
                    return 'mdi-ferry'
                case 'manual':
                    return 'mdi-map-marker-outline'
                default:
                    return 'mdi-city'
            }
        }
    }

    export default OfflineService
</script>

<style lang="scss" scoped>
    :deep(.v-dialog) {
        background: #fff;
        padding: 20px;
    }

    .modal-close-btn-block {
        position: absolute;
        top: 10px;
        right: 10px;
    }

    .make-new-line {
        white-space: pre-line;
    }
</style>
